import React, { useEffect, useRef, useState } from 'react';
import { NavLink, Outlet, useNavigate, useLocation } from 'react-router-dom';
import AIAssistant from './AIAssistant';
import PreguntarBuzon from './PreguntarBuzon';
import { useAuth } from '../context/AuthContext';
import { Logo } from './Logo';
import { Button } from './ui/button';
import {
  LayoutDashboard, Users, ScrollText, LogOut, Building2, Layers, Network,
  UserSquare2, FileStack, CheckSquare, Package, ShieldCheck, Briefcase,
  SlidersHorizontal, Map, KeyRound, Database, Settings, ChevronDown, Bot,
  Receipt, Target, Sparkles, Menu, X, CalendarCheck2, Clock, ClipboardList, Merge, FileBarChart,
  Inbox, MessageCircleQuestion, Brain, Warehouse, Tag, Boxes, FileInput, ToggleLeft,
  Newspaper, BarChart2 } from 'lucide-react';

const ROL_LABEL = {
  admin: 'Administrador',
  dir_comercial: 'Director Comercial',
  comercial: 'Agente Comercial',
  agente_libre: 'Agente Libre',
  admin_comercial: 'Admin Comercial',
};

// ── Mapa item de nav → recurso de permisos. Solo se filtra si el item
// aparece en este mapa (resto se asume público). Usado por hasPermission.
const NAV_PERMISO_MAP = {
  '/correos': 'correos',
  '/informes': 'informes',
};

// ── Item único siempre visible ─────────────────────────────────────
const primaryItem = { to: '/', label: 'Dashboard', icon: LayoutDashboard, flag: 'nav_dashboard' };

// ── Agrupaciones en dropdowns ──────────────────────────────────────
const groups = [
  {
    id: 'crm',
    label: 'CRM',
    icon: Building2,
    items: [
      { to: '/clientes',   label: 'Empresas',   icon: Building2,      flag: 'nav_clientes' },
      { to: '/contactos',  label: 'Contactos',  icon: UserSquare2,    flag: 'nav_contactos' },
      { to: '/contratos',  label: 'Contratos',  icon: FileStack,      flag: 'nav_contratos' },
      { to: '/visitas',    label: 'Visitas',    icon: CalendarCheck2, flag: 'nav_visitas' },
      { to: '/proyectos',  label: 'Proyectos',  icon: Briefcase,      flag: 'nav_proyectos' },
      { to: '/tareas',     label: 'Tareas',     icon: CheckSquare,    flag: 'nav_tareas' },
      { to: '/correos',    label: 'Correos',    icon: Inbox,          flag: 'nav_correos' },
    ],
  },
  {
    id: 'ventas',
    label: 'Ventas',
    icon: Receipt,
    items: [
      { to: '/documentos-comerciales', label: 'Ventas y Métricas', icon: Receipt,       flag: 'nav_documentos_comerciales' },
      { to: '/informes',               label: 'Informes',          icon: FileBarChart },
      { to: '/pedidos',                label: 'Pedidos',           icon: ClipboardList, flag: 'nav_pedidos' },
      // { to: '/depositos', label: 'Depósitos', icon: Warehouse },  // desconectado — datos AMD incompletos
      { to: '/tarifas',               label: 'Tarifas',            icon: Tag,           flag: 'nav_tarifas' },
      { to: '/homologaciones',         label: 'Homologaciones',    icon: ShieldCheck,   flag: 'nav_homologaciones' },
      { to: '/productos',              label: 'Productos',         icon: Package,       flag: 'nav_productos' },
      { to: '/articulos',              label: 'Artículos ERP',     icon: Boxes,         flag: 'nav_articulos' },
    ],
  },
  {
    id: 'ia',
    label: 'IA',
    icon: Sparkles,
    highlight: true,
    items: [
      { to: '/prospeccion',     label: 'Prospección',     icon: Target,   flag: 'nav_prospeccion' },
      { to: '/mapa-municipios', label: 'Mapa municipios', icon: Map,      flag: 'nav_mapa_municipios' },
      { to: '/plan-semanal',    label: 'Plan semanal IA', icon: Sparkles, flag: 'nav_plan_semanal' },
      { to: '/enriquecimiento', label: 'Enriquecer CRM',  icon: Brain,    flag: 'nav_enriquecimiento' },
    ],
  },
];

// Ítems directos (no dropdown): se renderizan entre "Ventas" e "IA".
const noticiasItem = { to: '/noticias', label: 'Noticias', icon: Newspaper };
const sectorItem = { to: '/sector', label: 'Sector', icon: BarChart2 };

// superAdmin:true → configuración del sistema (solo admin/dir_comercial).
// Resto → gestión comercial avanzada (incluye admin_comercial).
const adminMenu = [
  { to: '/grupos',                label: 'Grupos',          icon: Layers },
  { to: '/zonas',                 label: 'Zonas',           icon: Map,               superAdmin: true },
  { to: '/organigrama',           label: 'Organigrama',     icon: Network },
  { to: '/campos-personalizados', label: 'Campos',          icon: SlidersHorizontal, superAdmin: true },
  { to: '/bots-ia',               label: 'Bots IA',         icon: Bot },
  { to: '/dashboard-config',      label: 'Dashboard cfg',   icon: LayoutDashboard,   superAdmin: true },
  { to: '/usuarios',              label: 'Usuarios',        icon: Users,             superAdmin: true },
  { to: '/permisos',              label: 'Permisos',        icon: KeyRound,          superAdmin: true },
  { to: '/admin/feature-flags',   label: 'Funciones',       icon: ToggleLeft,        superAdmin: true },
  { to: '/integraciones-erp',     label: 'ERP',             icon: Database },
  { to: '/erp/importar',          label: 'Import. ERP',     icon: FileInput },
  { to: '/configuracion-apis',    label: 'APIs',            icon: KeyRound,          superAdmin: true },
  { to: '/scheduler',             label: 'Scheduler',       icon: Clock,             superAdmin: true },
  { to: '/admin/ia-stats',        label: 'IA stats',        icon: Sparkles },
  { to: '/admin/dedupe-gestoras', label: 'Dedupe gestoras', icon: Merge,             superAdmin: true },
  { to: '/auditoria',             label: 'Auditoría',       icon: ScrollText,        superAdmin: true },
];

// Filtra el menú admin: items superAdmin solo para isSuperAdmin.
const filtrarAdminMenu = (isSuperAdmin) =>
  adminMenu.filter((it) => !it.superAdmin || isSuperAdmin);

// ── Dropdown reutilizable ──────────────────────────────────────────
const NavDropdown = ({ id, label, icon: Icon, items, highlight = false, currentPath }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const isActive = items.some((i) => currentPath === i.to || (i.to !== '/' && currentPath.startsWith(i.to)));

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);
  useEffect(() => { setOpen(false); }, [currentPath]);

  const activeCls = highlight
    ? 'bg-violet-50 text-violet-700'
    : 'bg-brand-50 text-brand-700';

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid={`nav-group-${id}`}
        aria-expanded={open}
        aria-haspopup="menu"
        className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-sm font-medium transition-all whitespace-nowrap ${
          isActive || open ? activeCls : 'text-ink-600 hover:bg-ink-100 hover:text-ink-900'
        }`}
      >
        <Icon className="w-4 h-4" />
        {label}
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div
          className="absolute left-0 top-full mt-1 min-w-[230px] bg-white border border-ink-200 rounded-lg shadow-lg py-1 z-30"
          data-testid={`dropdown-${id}`}
          role="menu"
        >
          {items.map(({ to, label: l, icon: I }) => (
            <NavLink
              key={to}
              to={to}
              data-testid={`nav-${l.toLowerCase().replace(/\s/g, '-')}`}
              className={({ isActive: a }) =>
                `flex items-center gap-3 px-4 py-2 text-sm transition-colors ${
                  a ? 'bg-brand-50 text-brand-700 font-medium' : 'text-ink-700 hover:bg-ink-50'
                }`
              }
              role="menuitem"
            >
              <I className="w-4 h-4 shrink-0" />
              {l}
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
};

// ── Menú móvil: drawer lateral ────────────────────────────────────
const MobileDrawer = ({ open, onClose, isAdmin, isSuperAdmin, currentPath, hasPermission, hasFeature }) => {
  if (!open) return null;
  const filtrar = (items) => items.filter((it) => {
    const rec = NAV_PERMISO_MAP[it.to];
    if (rec && !hasPermission(rec, 'leer')) return false;
    if (it.flag && !hasFeature(it.flag)) return false;
    return true;
  });
  const allItems = [
    ...(hasFeature(primaryItem.flag) ? [primaryItem] : []),
    ...groups.flatMap((g) => filtrar(g.items)),
    noticiasItem,
    sectorItem,
    ...(isAdmin ? filtrarAdminMenu(isSuperAdmin) : []),
  ];
  return (
    <div className="fixed inset-0 z-40 md:hidden" data-testid="mobile-drawer">
      <div className="absolute inset-0 bg-ink-900/40" onClick={onClose} />
      <div className="absolute top-0 right-0 bottom-0 w-72 bg-white shadow-xl flex flex-col">
        <div className="flex items-center justify-between px-4 h-14 border-b border-ink-200">
          <Logo size="sm" />
          <button onClick={onClose} className="p-2 hover:bg-ink-100 rounded" aria-label="Cerrar menú">
            <X className="w-5 h-5 text-ink-600" />
          </button>
        </div>
        <nav className="flex-1 overflow-y-auto py-2">
          {allItems.map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              end={to === '/'}
              onClick={onClose}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 text-sm border-l-4 transition-colors ${
                  isActive
                    ? 'bg-brand-50 text-brand-700 border-brand-500 font-medium'
                    : 'text-ink-700 border-transparent hover:bg-ink-50'
                }`
              }
            >
              <Icon className="w-4 h-4 shrink-0" />
              {label}
            </NavLink>
          ))}
        </nav>
      </div>
    </div>
  );
};

export const AppShell = () => {
  const { user, logout, isAdmin, isSuperAdmin, hasPermission, hasFeature } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [askOpen, setAskOpen] = useState(false);

  // Filtra los items de nav según permisos de rol y feature flags.
  const filtrarItems = (items) => items.filter((it) => {
    const rec = NAV_PERMISO_MAP[it.to];
    if (rec && !hasPermission(rec, 'leer')) return false;
    if (it.flag && !hasFeature(it.flag)) return false;
    return true;
  });
  const groupsFiltered = groups
    .map((g) => ({ ...g, items: filtrarItems(g.items) }))
    .filter((g) => g.items.length > 0);

  // Atajo global Ctrl/Cmd+K → abre PreguntarBuzon (solo si tiene permiso y feature activa)
  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key && e.key.toLowerCase() === 'k') {
        if (!hasPermission('correos', 'leer') || !hasFeature('feat_correos_preguntar')) return;
        e.preventDefault();
        setAskOpen((v) => !v);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [hasPermission, hasFeature]);

  const handleLogout = () => { logout(); navigate('/'); };

  return (
    <div className="min-h-screen bg-ink-50">
      <header className="bg-white border-b border-ink-200 sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-4 lg:px-6 h-16 flex items-center justify-between gap-3">
          {/* Logo + nav agrupada */}
          <div className="flex items-center gap-4 lg:gap-6 min-w-0 flex-1">
            <NavLink to="/" className="shrink-0"><Logo size="md" /></NavLink>

            <nav className="hidden md:flex items-center gap-1 flex-wrap" data-testid="main-nav">
              {/* Dashboard siempre visible */}
              <NavLink
                to="/"
                end
                data-testid="nav-dashboard"
                className={({ isActive }) =>
                  `inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-sm font-medium transition-all whitespace-nowrap ${
                    isActive ? 'bg-brand-50 text-brand-700' : 'text-ink-600 hover:bg-ink-100 hover:text-ink-900'
                  }`
                }
              >
                <LayoutDashboard className="w-4 h-4" />
                <span className="hidden lg:inline">Dashboard</span>
              </NavLink>

              {/* Grupos — "Noticias" se inserta como item directo antes del grupo IA */}
              {groupsFiltered.map((g) => (
                <React.Fragment key={g.id}>
                  {g.id === 'ia' && (
                    <>
                      <NavLink
                        to="/noticias"
                        data-testid="nav-noticias"
                        className={({ isActive }) =>
                          `inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-sm font-medium transition-all whitespace-nowrap ${
                            isActive ? 'bg-brand-50 text-brand-700' : 'text-ink-600 hover:bg-ink-100 hover:text-ink-900'
                          }`
                        }
                      >
                        <Newspaper className="w-4 h-4" />
                        <span className="hidden lg:inline">Noticias</span>
                      </NavLink>
                      <NavLink
                        to="/sector"
                        data-testid="nav-sector"
                        className={({ isActive }) =>
                          `inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-sm font-medium transition-all whitespace-nowrap ${
                            isActive ? 'bg-brand-50 text-brand-700' : 'text-ink-600 hover:bg-ink-100 hover:text-ink-900'
                          }`
                        }
                      >
                        <BarChart2 className="w-4 h-4" />
                        <span className="hidden lg:inline">Sector</span>
                      </NavLink>
                    </>
                  )}
                  <NavDropdown
                    id={g.id}
                    label={g.label}
                    icon={g.icon}
                    items={g.items}
                    highlight={g.highlight}
                    currentPath={location.pathname}
                  />
                </React.Fragment>
              ))}

              {/* Admin */}
              {isAdmin && (
                <NavDropdown
                  id="admin"
                  label="Admin"
                  icon={Settings}
                  items={filtrarAdminMenu(isSuperAdmin)}
                  currentPath={location.pathname}
                />
              )}
            </nav>
          </div>

          {/* Usuario + logout */}
          <div className="flex items-center gap-2 lg:gap-3 shrink-0">
            {hasPermission('correos', 'leer') && hasFeature('feat_correos_preguntar') && (
              <button
                onClick={() => setAskOpen(true)}
                className="hidden md:inline-flex items-center gap-1.5 px-2 py-1.5 rounded border border-ink-200 hover:border-violet-300 hover:bg-violet-50 text-xs text-ink-600 transition"
                title="Preguntar al buzón (Ctrl/Cmd+K)"
                data-testid="btn-preguntar-buzon-global"
              >
                <MessageCircleQuestion className="w-3.5 h-3.5 text-violet-600" />
                <span className="hidden lg:inline">Preguntar</span>
                <kbd className="bg-ink-100 text-ink-500 rounded px-1 text-[10px] font-mono">⌘K</kbd>
              </button>
            )}
            <div className="text-right hidden xl:block">
              <p className="text-sm font-semibold text-ink-900 leading-tight truncate max-w-[180px]" data-testid="user-name">
                {user?.nombre} {user?.apellidos || ''}
              </p>
              <p className="text-xs text-ink-500 leading-tight truncate max-w-[180px]" data-testid="user-rol">
                {ROL_LABEL[user?.rol] || user?.rol}
                {user?.provincias_asignadas?.length > 0 && (
                  <> · {user.provincias_asignadas.length} prov.</>
                )}
              </p>
            </div>
            <button
              onClick={() => navigate('/perfil')}
              className="h-9 w-9 rounded-full bg-brand-500 flex items-center justify-center text-white font-bold shadow-sm text-sm shrink-0 hover:bg-brand-600 transition cursor-pointer"
              title={`${user?.nombre || ''} · Mi perfil`}
              data-testid="btn-perfil"
            >
              {user?.nombre?.[0]?.toUpperCase() || 'U'}
            </button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              data-testid="logout-btn"
              className="text-ink-600 hover:text-ink-900 hidden sm:inline-flex"
            >
              <LogOut className="w-4 h-4 sm:mr-1.5" />
              <span className="hidden lg:inline">Salir</span>
            </Button>
            {/* Botón menú móvil */}
            <button
              onClick={() => setDrawerOpen(true)}
              className="md:hidden p-2 hover:bg-ink-100 rounded"
              aria-label="Abrir menú"
              data-testid="mobile-menu-btn"
            >
              <Menu className="w-5 h-5 text-ink-700" />
            </button>
          </div>
        </div>
      </header>

      {/* Drawer móvil */}
      <MobileDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        isAdmin={isAdmin}
        isSuperAdmin={isSuperAdmin}
        currentPath={location.pathname}
        hasPermission={hasPermission}
        hasFeature={hasFeature}
      />

      {/* Page content */}
      <main className="max-w-7xl mx-auto px-4 lg:px-6 py-8 lg:py-10">
        <Outlet />
      </main>

      <AIAssistant />
      {hasPermission('correos', 'leer') && hasFeature('feat_correos_preguntar') && <PreguntarBuzon open={askOpen} onOpenChange={setAskOpen} />}
    </div>
  );
};

export default AppShell;
